import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { TableColumnHeaders } from './TableColumnHeaders';
import { TableBody } from './TableBody';
import { logger } from '../../../../utils/logger';
import { Table } from '@tanstack/react-table';
import { useStatusBarInfo } from '../../../../hooks/useStatusBarInfo';

interface Column {
  id: string;
  name: string;
  type: string;
  orderIndex: number;
  isVisible: boolean;
}

interface Row {
  id: string;
  orderIndex: number;
}

interface Cell {
  id: string;
  rowId: string;
  columnId: string;
  value: string;
}

interface TableDefinition {
  id: string;
  name: string;
  topicId: string;
}

interface TableContainerProps {
  horizontalScrollRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  table: Table<Record<string, unknown>>;
  columns: Column[];
  rows: Row[];
  cells: Cell[];
  tableDefinition: TableDefinition;
  editingColumnId: string | null;
  setEditingColumnId: React.Dispatch<React.SetStateAction<string | null>>;
  showColumnTypeMenu: string | null;
  setShowColumnTypeMenu: React.Dispatch<React.SetStateAction<string | null>>;
  editingCell: { rowId: string; columnId: string } | null;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; columnId: string } | null>>;
  cellMenuOpen: { rowId: string; columnId: string } | null;
  setCellMenuOpen: React.Dispatch<React.SetStateAction<{ rowId: string; columnId: string } | null>>;
  updateColumn: (tableId: string, columnId: string, data: Partial<Column>) => Promise<void>;
  deleteColumn: (tableId: string, columnId: string) => Promise<void>;
  deleteRow: (tableId: string, rowId: string) => Promise<void>;
  updateCell: (rowId: string, columnId: string, value: string) => Promise<void>;
  updateRow: (tableId: string, rowId: string, updates: Partial<Row>) => Promise<Row>;
  addRow: (tableId: string, data: Partial<Row>) => Promise<Row>;
  formatValueForDisplay: (value: unknown, type: string) => string;
  ColumnTypeMenu: React.ComponentType<{
    column: Column;
    onTypeChange: (type: string) => void;
    onClose: () => void;
  }>;
  onAddColumnAfter?: (columnId: string) => void;
  handleAddRow?: () => void;
}

export function TableContainer({
  horizontalScrollRef,
  isMobile,
  table,
  columns,
  rows,
  cells,
  tableDefinition,
  editingColumnId,
  setEditingColumnId,
  showColumnTypeMenu,
  setShowColumnTypeMenu,
  editingCell,
  setEditingCell,
  cellMenuOpen,
  setCellMenuOpen,
  updateColumn,
  deleteColumn,
  deleteRow,
  updateCell,
  updateRow,
  addRow,
  formatValueForDisplay,
  ColumnTypeMenu,
  onAddColumnAfter,
  handleAddRow,
}: TableContainerProps) {
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  // Add a state variable to track viewport mode for re-rendering
  const [viewportMode, setViewportMode] = useState<'mobile' | 'desktop'>(
    isMobile ? 'mobile' : 'desktop'
  );

  // Force re-render when isMobile changes
  useEffect(() => {
    const currentMode = isMobile ? 'mobile' : 'desktop';
    if (viewportMode !== currentMode) {
      logger.component('Viewport mode changed in TableContainer', {
        from: viewportMode,
        to: currentMode,
        isMobile: isMobile ? 'true' : 'false',
      });
      setViewportMode(currentMode);

      // Force table re-calculation by calling recomputePosition on the scroll container
      if (horizontalScrollRef.current) {
        horizontalScrollRef.current.scrollLeft = 0;
      }
    }
  }, [isMobile, viewportMode, horizontalScrollRef]);
  // Calculate if we need horizontal scrolling on desktop
  // Column widths are set directly in the JSX below

  // On desktop, if there are more than 4 text columns, we should use scrolling mode
  // This gives more reasonable column widths
  const hasEnoughColumnsToNeedScrolling = () => {
    if (isMobile) return true; // Mobile always scrolls

    // Set a baseline threshold for scrolling
    if (columns.length > 5) return true;

    // Count columns by type to determine if we need scrolling
    const textColumnsCount = columns.filter(
      col => col.type === 'text' || col.type === 'date' || !col.type
    ).length;

    // More than 3 text columns usually needs scrolling for reasonable widths
    return textColumnsCount > 3;
  };

  // Force desktop scrolling mode when we have many columns
  // Initialize to true for tables with many columns by default
  const [needsDesktopScrolling, setNeedsDesktopScrolling] = useState(columns.length > 5);

  // Initial check for scrolling mode on mount
  useEffect(() => {
    // Force into scrolling mode immediately if we have enough columns
    if (!isMobile && columns.length > 0) {
      const shouldScroll = hasEnoughColumnsToNeedScrolling();
      setNeedsDesktopScrolling(shouldScroll);

      logger.component('Initial table scrolling mode set', {
        needsScrolling: shouldScroll,
        columnCount: columns.length,
        textColumnsCount: columns.filter(
          col => col.type === 'text' || col.type === 'date' || !col.type
        ).length,
      });
    }
  }, []); // Run once on mount

  // Update scrolling mode when columns or viewport changes
  useEffect(() => {
    if (!isMobile) {
      const shouldScroll = hasEnoughColumnsToNeedScrolling();
      setNeedsDesktopScrolling(shouldScroll);

      logger.component('Table scrolling mode updated', {
        needsScrolling: shouldScroll,
        columnCount: columns.length,
        viewportMode,
      });
    } else {
      setNeedsDesktopScrolling(false); // Not needed for mobile (handled differently)
    }
  }, [columns.length, isMobile, viewportMode]);

  return (
    <div className="flex-1 flex flex-col relative h-full">
      {/* This 'Add Row' button will be shown after the table */}

      {/* Combined vertical and horizontal scrolling container */}
      <div
        className={`relative w-full h-full ${
          isMobile || needsDesktopScrolling ? 'overflow-auto' : 'overflow-y-auto overflow-x-hidden'
        } touch-pan-x touch-pan-y overscroll-contain border-b border-gray-200`}
        ref={horizontalScrollRef}
        style={{
          width: '100%',
          minWidth: '100%',
          maxWidth: '100vw', // Ensure it doesn't exceed viewport width
          height: isMobile ?
            isNativeApp ?
              `calc(100vh - 60px - ${statusBarHeight}px - ${navigationBarHeight}px)` :
              'calc(100vh - 60px)'
            : '100%', // Account for header (no bottom button on mobile), status bar, and nav bar on mobile
          paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
          paddingBottom: isMobile && isNativeApp ? `${navigationBarHeight}px` : '0',
          msOverflowStyle: 'auto',
          transform: 'translateZ(0)', // Hardware acceleration
          willChange: 'scroll-position', // Optimize for scrolling
          position: 'relative', // Needed for sticky positioning context
          isolation: 'isolate', // Creates a new stacking context for better sticky behavior
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          scrollbarWidth: 'thin', // Thinner scrollbars on supporting browsers
          scrollbarColor: '#CBD5E0 #F7FAFC', // Custom scrollbar colors on supporting browsers
          overscrollBehavior: 'contain', // Prevent page scroll when table scroll reaches end
        }}
      >
        <table
          key={`table-${viewportMode}-${needsDesktopScrolling ? 'scrollable' : 'fixed'}`}
          className={`border-collapse table-fixed my-0 relative border-b border-l border-gray-200 ${needsDesktopScrolling ? 'table-scrollable' : ''}`}
          style={{
            width: isMobile || needsDesktopScrolling ? 'max-content' : '100%',
            minWidth: isMobile || needsDesktopScrolling ? 'max-content' : '100%',
            borderSpacing: 0,
            borderCollapse: 'separate',
            // Add global CSS variables that child components can access
            '--sticky-border-color': 'rgba(0,0,0,0)',
            '--non-sticky-border-color': '#e5e7eb', // gray-200
          }}
        >
          <colgroup
            key={`colgroup-${viewportMode}-${needsDesktopScrolling ? 'scrollable' : 'fixed'}`}
          >
            {/* Line numbers column - always sticky */}
            <col style={{ width: '20px', minWidth: '20px' }} />
            {columns.map(column => (
              <col
                key={column.id}
                style={{
                  width:
                    isMobile || needsDesktopScrolling
                      ? column.type === 'boolean'
                        ? '80px' // Fixed width for boolean columns in scrollable mode
                        : column.type === 'number'
                          ? '120px' // Fixed width for number columns in scrollable mode
                          : '240px' // Fixed width for text columns in scrollable mode
                      : 'auto', // Responsive width on desktop fixed mode
                  minWidth:
                    isMobile || needsDesktopScrolling
                      ? column.type === 'boolean'
                        ? '80px' // Fixed min-width for columns in scrollable mode
                        : column.type === 'number'
                          ? '120px'
                          : '240px'
                      : column.type === 'boolean'
                        ? '60px' // Minimum width on desktop fixed mode
                        : column.type === 'number'
                          ? '80px'
                          : '120px',
                  maxWidth:
                    !(isMobile || needsDesktopScrolling) &&
                    column.type !== 'boolean' &&
                    column.type !== 'number'
                      ? '1fr' // Allow text columns to grow only in desktop fixed mode
                      : undefined,
                }}
              />
            ))}
          </colgroup>

          <TableColumnHeaders
            key={`headers-${viewportMode}-${needsDesktopScrolling ? 'scrollable' : 'fixed'}`}
            table={table}
            editingColumnId={editingColumnId}
            setEditingColumnId={setEditingColumnId}
            showColumnTypeMenu={showColumnTypeMenu}
            setShowColumnTypeMenu={setShowColumnTypeMenu}
            columns={columns}
            updateColumn={updateColumn}
            deleteColumn={deleteColumn}
            tableDefinition={tableDefinition}
            isMobile={isMobile}
            needsDesktopScrolling={needsDesktopScrolling}
            ColumnTypeMenu={ColumnTypeMenu}
            onAddColumnAfter={onAddColumnAfter}
          />

          <TableBody
            key={`body-${viewportMode}-${needsDesktopScrolling ? 'scrollable' : 'fixed'}`}
            table={table}
            columns={columns}
            rows={rows}
            cells={cells}
            isMobile={isMobile}
            needsDesktopScrolling={needsDesktopScrolling}
            editingCell={editingCell}
            setEditingCell={setEditingCell}
            cellMenuOpen={cellMenuOpen}
            setCellMenuOpen={setCellMenuOpen}
            formatValueForDisplay={formatValueForDisplay}
            tableDefinition={tableDefinition}
            deleteRow={deleteRow}
            updateCell={updateCell}
            updateRow={updateRow}
            addRow={addRow}
            handleAddRow={handleAddRow}
          />
        </table>
      </div>

      {/* Add Row button - positioned directly after the table - only show on desktop */}
      {handleAddRow && !isMobile && (
        <div className="pt-4 pl-4 pb-4">
          <button
            onClick={handleAddRow}
            className="flex items-center justify-center text-blue-600 hover:text-blue-700 py-2 px-5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
          >
            <Plus size={16} className="mr-2" />
            <span className="font-medium">Add New Row</span>
          </button>
        </div>
      )}
    </div>
  );
}
